import React, { useEffect, useState } from "react";
import {
  getPatientPreference,
  updatePatientPreference,
  deletePatientPreference,
} from "../APIs/patientService";
import { getSpecialties, getInsurancePlans } from "../APIs/dropdownApi";
import { getStates, getCitiesByState } from "../APIs/geoApi";
import {
  ETHNICITIES,
  LANGUAGES,
  GENDER,
} from "../DropdownOptions/LanguageEthnicityGender";
import usStates from "../data/us-states.json";

const PatientPreferencesPage = ({ patientId }) => {
  // Dropdown data
  const [specialties, setSpecialties] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [form, setForm] = useState({
    specialty: "",
    stateId: "",
    state: "",
    cityId: "",
    city: "",
    insuranceIds: [],
    languageNames: [],
    ethnicity: "",
    gender: "",
    minYearsOfExperience: "",
    maxYearsOfExperience: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load dropdown data
  useEffect(() => {
    getSpecialties().then((data) => setSpecialties(data || []));
    getInsurancePlans().then((data) => setInsurances(data || []));

    (async () => {
      setLoadingStates(true);
      try {
        const states = await getStates();
        setStateOptions(
          (states || []).map((s) => ({
            value: String(s.id),
            label: s.name,
            code:
              s.stateCode ||
              (usStates.find((u) => u.name === s.name)?.code ?? ""),
          })),
        );
      } finally {
        setLoadingStates(false);
      }
    })();
  }, []);

  // Load cities when stateId changes
  useEffect(() => {
    if (!form.stateId) {
      setCityOptions([]);
      return;
    }

    let on = true;
    (async () => {
      setLoadingCities(true);
      try {
        const cities = await getCitiesByState(Number(form.stateId));
        if (on) {
          setCityOptions(
            (cities || []).map((c) => ({ value: String(c.id), label: c.name })),
          );
        }
      } finally {
        if (on) setLoadingCities(false);
      }
    })();

    return () => {
      on = false;
    };
  }, [form.stateId]);

  // Load preferences
  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getPatientPreference(patientId);
        if (!data) {
          setLoading(false);
          return;
        }

        // Find matching state
        const matchedState = stateOptions.find((s) => s.code === data.state);
        const stateId = matchedState?.value || "";

        setForm({
          specialty: data.specialty ?? "",
          stateId: stateId,
          state: data.state ?? "",
          cityId: "", // Will be set after cities load
          city: data.city ?? "",
          insuranceIds: data.planNames ? [data.planNames] : [],
          languageNames: data.languages ? [data.languages] : [],
          ethnicity: data.ethnicity ?? "",
          gender: data.gender ?? "",
          minYearsOfExperience: data.minYearsOfExperience ?? "",
          maxYearsOfExperience: data.maxYearsOfExperience ?? "",
        });

        // If we have a state, load cities and then set cityId
        if (stateId && data.city) {
          const cities = await getCitiesByState(Number(stateId));
          const matchedCity = cities.find((c) => c.name === data.city);
          if (matchedCity) {
            setForm((prev) => ({ ...prev, cityId: String(matchedCity.id) }));
          }
        }
      } catch (err) {
        console.error("Error fetching preferences:", err);
      } finally {
        setLoading(false);
      }
    };

    // Wait for states to load before fetching preferences
    if (stateOptions.length > 0) {
      fetch();
    }
  }, [patientId, stateOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "stateId") {
      const selected = stateOptions.find(
        (s) => String(s.value) === String(value),
      );
      setForm((prev) => ({
        ...prev,
        stateId: value,
        state: selected?.code || "",
        cityId: "",
        city: "",
      }));
      return;
    }

    if (name === "cityId") {
      const cityName =
        cityOptions.find((c) => String(c.value) === String(value))?.label || "";
      setForm((prev) => ({
        ...prev,
        cityId: value,
        city: cityName,
      }));
      return;
    }

    if (name === "insuranceIds") {
      setForm((prev) => ({ ...prev, insuranceIds: value ? [value] : [] }));
      return;
    }

    if (name === "languageNames") {
      setForm((prev) => ({
        ...prev,
        languageNames: value ? [String(value)] : [],
      }));
      return;
    }

    if (name === "minYearsOfExperience" || name === "maxYearsOfExperience") {
      const parsed = value === "" ? "" : Number(value);
      setForm((prev) => ({ ...prev, [name]: parsed }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const updated = await updatePatientPreference(patientId, form);

      if (
        updated &&
        (updated.patientId || updated.specialty || updated.updatedAt)
      ) {
        // Re-fetch to ensure we have correct data
        const fresh = await getPatientPreference(patientId);

        const matchedState = stateOptions.find((s) => s.code === fresh.state);
        const stateId = matchedState?.value || "";

        setForm({
          specialty: fresh.specialty ?? "",
          stateId: stateId,
          state: fresh.state ?? "",
          cityId: form.cityId, // Keep current cityId
          city: fresh.city ?? "",
          insuranceIds: fresh.planNames ? [fresh.planNames] : [],
          languageNames:
            fresh.languageNames || fresh.languages
              ? [fresh.languageNames || fresh.languages]
              : [],
          ethnicity: fresh.ethnicity ?? "",
          gender: fresh.gender ?? "",
          minYearsOfExperience: fresh.minYearsOfExperience ?? "",
          maxYearsOfExperience: fresh.maxYearsOfExperience ?? "",
        });
      }

      alert("Preferences updated successfully.");
    } catch (error) {
      console.error("Update failed:", error);
      if (error.response) {
        alert(
          `Update failed: ${error.response.status} - ${JSON.stringify(
            error.response.data,
          )}`,
        );
      } else {
        alert(`Update failed: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Are you sure you want to clear your preferences?"))
      return;
    try {
      await deletePatientPreference(patientId);
      setForm({
        specialty: "",
        stateId: "",
        state: "",
        cityId: "",
        city: "",
        insuranceIds: [],
        languageNames: [],
        ethnicity: "",
        gender: "",
        minYearsOfExperience: "",
        maxYearsOfExperience: "",
      });
      alert("Preferences cleared.");
    } catch (err) {
      console.error("Error clearing preferences:", err);
      alert("Failed to clear preferences.");
    }
  };

  const renderSelect = (label, name, options, disabled = false) => {
    const current = form[name];
    const selected = Array.isArray(current)
      ? (current[0] ?? "")
      : (current ?? "");

    return (
      <div className="flex items-center gap-4" key={name}>
        <label htmlFor={name} className="w-48 font-medium text-gray-700">
          {label}
        </label>
        <select
          id={name}
          name={name}
          value={selected}
          onChange={handleChange}
          disabled={disabled}
          className={`flex-1 border border-gray-300 rounded px-3 py-2 ${
            disabled ? "bg-gray-100 text-gray-500" : ""
          }`}
        >
          <option value="">Select {label.toLowerCase()}</option>
          {options.map((opt) => {
            if (typeof opt === "string") {
              return (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              );
            }
            const v = opt.value ?? opt.id;
            const lbl = opt.label ?? opt.name ?? opt.planName ?? String(v);
            return (
              <option key={String(v)} value={v}>
                {lbl}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  // Validation
  const isFormValid =
    form.specialty &&
    form.stateId &&
    form.cityId &&
    form.insuranceIds.length > 0 &&
    (form.minYearsOfExperience === "" ||
      form.maxYearsOfExperience === "" ||
      Number(form.maxYearsOfExperience) >= Number(form.minYearsOfExperience));

  if (loading) return <p className="p-4">Loading preferences...</p>;

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow rounded-2xl">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Update Patient Preferences
      </h2>

      <div className="space-y-4">
        {renderSelect(
          "Specialty",
          "specialty",
          (specialties || []).map((s) => ({ value: s.name, label: s.name })),
        )}

        {renderSelect(
          loadingStates ? "Preferred State (loading…)" : "Preferred State",
          "stateId",
          stateOptions,
        )}

        {renderSelect(
          loadingCities ? "Preferred City (loading…)" : "Preferred City",
          "cityId",
          cityOptions,
          !form.stateId || loadingCities,
        )}

        {renderSelect(
          "Insurance Accepted",
          "insuranceIds",
          (insurances || []).map((i) => ({
            value: i.planName,
            label: i.planName,
          })),
        )}

        {renderSelect(
          "Preferred Language",
          "languageNames",
          (LANGUAGES || []).map((l) =>
            typeof l === "string" ? l : (l.label ?? l.name),
          ),
        )}

        {renderSelect("Ethnicity", "ethnicity", ETHNICITIES)}

        {renderSelect("Gender", "gender", GENDER)}

        <div className="flex items-center gap-4">
          <label
            htmlFor="minYearsOfExperience"
            className="w-48 font-medium text-gray-700"
          >
            Min Years of Experience
          </label>
          <input
            type="number"
            id="minYearsOfExperience"
            name="minYearsOfExperience"
            min="0"
            value={form.minYearsOfExperience}
            onChange={handleChange}
            className="flex-1 border border-gray-300 rounded px-3 py-2"
          />
        </div>

        <div className="flex items-center gap-4">
          <label
            htmlFor="maxYearsOfExperience"
            className="w-48 font-medium text-gray-700"
          >
            Max Years of Experience
          </label>
          <div className="flex-1">
            <input
              type="number"
              id="maxYearsOfExperience"
              name="maxYearsOfExperience"
              min="0"
              value={form.maxYearsOfExperience}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2"
            />
            {/* ✅ Only show if BOTH fields have values AND max < min */}
            {form.minYearsOfExperience !== "" &&
              form.maxYearsOfExperience !== "" &&
              Number(form.minYearsOfExperience) > 0 &&
              Number(form.maxYearsOfExperience) > 0 &&
              Number(form.maxYearsOfExperience) <
                Number(form.minYearsOfExperience) && (
                <p className="text-red-600 text-sm mt-1">
                  Max must be greater than or equal to Min (
                  {form.minYearsOfExperience})
                </p>
              )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={handleUpdate}
          disabled={saving || !isFormValid}
          className={`px-4 py-2 rounded-lg ${
            saving || !isFormValid
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {saving ? "Saving..." : "Update Preferences"}
        </button>

        <button
          onClick={handleClear}
          className="px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50"
        >
          Clear Preferences
        </button>
      </div>

      {!isFormValid && (
        <p className="text-red-600 text-sm mt-4">
          Complete specialty, state, city, and insurance before updating
          preferences.
        </p>
      )}
    </div>
  );
};

export default PatientPreferencesPage;
